import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, getContent, getSettings } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Gauge, Tag, Question, MapTrifold, CalendarBlank, UsersThree, Buildings, Stack,
  SlidersHorizontal, Handshake, Scales, Megaphone, GearSix, Tray, SignOut, House,
  Plus, Trash, FloppyDisk, PencilSimple, Cpu, ArrowLineDown, Fingerprint, BookOpen, Broadcast,
} from "@phosphor-icons/react";

const WD_NEXT = {
  KYC_PENDING: ["UNDER_REVIEW", "REJECTED", "CANCELLED"],
  UNDER_REVIEW: ["APPROVED", "REJECTED", "CANCELLED"],
  APPROVED: ["PROCESSING", "REJECTED"],
  PROCESSING: ["COMPLETED", "FAILED"],
  REQUESTED: ["UNDER_REVIEW", "CANCELLED"],
};

function ResourcesAdmin() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ title: "", type: "PDF", category: "Partner", url: "", desc: "" });
  const load = () => api.get("/resources").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!f.title || !f.url) { toast.error("Title and URL required"); return; }
    try { await api.post("/admin/resources", f); toast.success("Added"); setF({ title: "", type: "PDF", category: "Partner", url: "", desc: "" }); load(); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/resources/${id}`); load(); };
  const ip = "rounded-lg border border-white/10 bg-[#08090F] px-3 py-2 text-sm text-white";
  return (
    <div data-testid="admin-resources">
      <div className="rounded-xl border border-white/10 bg-[#0A0B10] p-5 mb-5 grid sm:grid-cols-2 gap-3">
        <input className={ip} placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="res-title" />
        <input className={ip} placeholder="URL (PDF/video link)" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} data-testid="res-url" />
        <select className={ip} value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>{["PDF", "Video", "Presentation", "Marketing Material", "Training", "Product Guide", "Compensation Plan", "Compliance Document", "FAQ"].map((t) => <option key={t} className="bg-[#08090F]">{t}</option>)}</select>
        <input className={ip} placeholder="Category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} />
        <input className={`${ip} sm:col-span-2`} placeholder="Description" value={f.desc} onChange={(e) => setF({ ...f, desc: e.target.value })} />
        <button onClick={add} data-testid="res-add" className="sm:col-span-2 px-4 py-2 rounded-full bg-[#2DD4BF] text-[#030305] text-sm font-semibold w-max"><Plus size={14} className="inline mr-1" />Add Resource</button>
      </div>
      <div className="space-y-2">
        {items.map((it) => (
          <div key={it.id} className="flex items-center justify-between rounded-lg border border-white/10 bg-[#0A0B10] px-4 py-3">
            <div><p className="text-sm text-white">{it.title} <span className="font-mono text-[10px] text-[#0E9E90]">{it.type}</span></p><p className="text-xs text-[#64748B] truncate max-w-md">{it.url}</p></div>
            <button onClick={() => del(it.id)} className="text-red-400 text-xs hover:underline">Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}

const SIG_EMPTY = { asset: "", direction: "LONG", entry: 0, target: 0, stop: 0, strategy: "Featured", note: "" };
const TR_EMPTY = { kind: "seminar", title: "", presenter: "Q-Core Research", desc: "", level: "All", url: "", starts_at: "", recorded_at: "", duration_min: 0 };

function SignalsAdmin() {
  const [tab, setTab] = useState("signals");
  const [signals, setSignals] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [sig, setSig] = useState(SIG_EMPTY);
  const [sigEdit, setSigEdit] = useState(null);
  const [tr, setTr] = useState(TR_EMPTY);
  const [trEdit, setTrEdit] = useState(null);
  const ip = "rounded-lg border border-white/10 bg-[#08090F] px-3 py-2 text-sm text-white w-full";

  const load = () => {
    api.get("/admin/signals").then((r) => setSignals(r.data)).catch(() => {});
    api.get("/admin/trainings").then((r) => setTrainings(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const saveSig = async () => {
    if (!sig.asset || !sig.entry || !sig.target) { toast.error("Asset, entry and target are required"); return; }
    const body = { ...sig, entry: Number(sig.entry), target: Number(sig.target), stop: Number(sig.stop) };
    try {
      if (sigEdit) { await api.put(`/admin/signals/${sigEdit}`, body); toast.success("Signal updated"); }
      else { await api.post("/admin/signals", body); toast.success("Signal published"); }
      setSig(SIG_EMPTY); setSigEdit(null); load();
    } catch { toast.error("Failed to save signal"); }
  };
  const editSig = (s) => { setSigEdit(s.id); setSig({ asset: s.asset, direction: s.direction, entry: s.entry, target: s.target, stop: s.stop || 0, strategy: s.strategy || "Featured", note: s.note || "" }); };
  const delSig = async (id) => { if (!window.confirm("Unpublish this signal?")) return; await api.delete(`/admin/signals/${id}`); load(); };

  const saveTr = async () => {
    if (!tr.title) { toast.error("Title is required"); return; }
    const body = { ...tr, duration_min: Number(tr.duration_min) || 0 };
    try {
      if (trEdit) { await api.put(`/admin/trainings/${trEdit}`, body); toast.success("Training updated"); }
      else { await api.post("/admin/trainings", body); toast.success("Training scheduled"); }
      setTr(TR_EMPTY); setTrEdit(null); load();
    } catch { toast.error("Failed to save training"); }
  };
  const editTr = (t) => { setTrEdit(t.id); setTr({ kind: t.kind, title: t.title, presenter: t.presenter || "", desc: t.desc || "", level: t.level || "All", url: t.url || "", starts_at: t.starts_at || "", recorded_at: t.recorded_at || "", duration_min: t.duration_min || 0 }); };
  const delTr = async (id) => { if (!window.confirm("Delete this training?")) return; await api.delete(`/admin/trainings/${id}`); load(); };

  return (
    <div data-testid="admin-signals-cms">
      <div className="flex gap-2 mb-5">
        {[["signals", "Quantum Signals"], ["trainings", "VIP Trainings"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`cms-tab-${k}`} className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === k ? "bg-[#2DD4BF] text-[#030305]" : "border border-white/15 text-[#94A3B8] hover:text-white"}`}>{l}</button>
        ))}
      </div>

      {tab === "signals" && (
        <div data-testid="cms-signals">
          <div className="rounded-xl border border-white/10 bg-[#0A0B10] p-5 mb-5">
            <p className="text-sm font-semibold text-white mb-3">{sigEdit ? "Edit Signal" : "Publish Featured Signal"}</p>
            <div className="grid sm:grid-cols-3 gap-3">
              <input className={ip} placeholder="Asset (e.g. BTC/USDT)" value={sig.asset} onChange={(e) => setSig({ ...sig, asset: e.target.value })} data-testid="sig-asset" />
              <select className={ip} value={sig.direction} onChange={(e) => setSig({ ...sig, direction: e.target.value })} data-testid="sig-direction">{["LONG", "SHORT"].map((x) => <option key={x} className="bg-[#08090F]">{x}</option>)}</select>
              <input className={ip} placeholder="Strategy" value={sig.strategy} onChange={(e) => setSig({ ...sig, strategy: e.target.value })} data-testid="sig-strategy" />
              <input type="number" step="any" className={ip} placeholder="Entry" value={sig.entry} onChange={(e) => setSig({ ...sig, entry: e.target.value })} data-testid="sig-entry" />
              <input type="number" step="any" className={ip} placeholder="Target" value={sig.target} onChange={(e) => setSig({ ...sig, target: e.target.value })} data-testid="sig-target" />
              <input type="number" step="any" className={ip} placeholder="Stop (optional)" value={sig.stop} onChange={(e) => setSig({ ...sig, stop: e.target.value })} data-testid="sig-stop" />
              <input className={`${ip} sm:col-span-3`} placeholder="Note (optional)" value={sig.note} onChange={(e) => setSig({ ...sig, note: e.target.value })} data-testid="sig-note" />
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={saveSig} data-testid="sig-save" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#2DD4BF] text-[#030305] text-sm font-semibold"><FloppyDisk size={14} /> {sigEdit ? "Update" : "Publish"}</button>
              {sigEdit && <button onClick={() => { setSig(SIG_EMPTY); setSigEdit(null); }} className="px-4 py-2 rounded-full border border-white/15 text-[#94A3B8] text-sm">Cancel</button>}
            </div>
          </div>
          <div className="space-y-2">
            {signals.length ? signals.map((s) => (
              <div key={s.id} className="flex items-center justify-between rounded-lg border border-white/10 bg-[#0A0B10] px-4 py-3" data-testid={`sig-row-${s.id}`}>
                <div className="min-w-0">
                  <p className="text-sm text-white">{s.asset} <span className={`font-mono text-[10px] ${s.direction === "LONG" ? "text-[#00E676]" : "text-[#FF2E63]"}`}>{s.direction}</span> <span className="font-mono text-[10px] text-[#2DD4BF]">{s.strategy}</span></p>
                  <p className="text-xs text-[#64748B] font-mono">Entry {s.entry} · Target {s.target}{s.stop ? ` · Stop ${s.stop}` : ""}{s.note ? ` · ${s.note}` : ""}</p>
                </div>
                <div className="flex gap-3 shrink-0">
                  <button onClick={() => editSig(s)} data-testid={`sig-edit-${s.id}`} className="text-[#2DD4BF] text-xs hover:underline flex items-center gap-1"><PencilSimple size={13} /> Edit</button>
                  <button onClick={() => delSig(s.id)} data-testid={`sig-unpublish-${s.id}`} className="text-red-400 text-xs hover:underline">Unpublish</button>
                </div>
              </div>
            )) : <p className="text-sm text-[#64748B] font-mono">No featured signals published. Live auto-signals still show to members.</p>}
          </div>
        </div>
      )}

      {tab === "trainings" && (
        <div data-testid="cms-trainings">
          <div className="rounded-xl border border-white/10 bg-[#0A0B10] p-5 mb-5">
            <p className="text-sm font-semibold text-white mb-3">{trEdit ? "Edit Training" : "Schedule Training / Seminar"}</p>
            <div className="grid sm:grid-cols-3 gap-3">
              <select className={ip} value={tr.kind} onChange={(e) => setTr({ ...tr, kind: e.target.value })} data-testid="tr-kind">{[["seminar", "Live Seminar"], ["recording", "Recording"], ["writeup", "Write-up"]].map(([v, l]) => <option key={v} value={v} className="bg-[#08090F]">{l}</option>)}</select>
              <input className={`${ip} sm:col-span-2`} placeholder="Title" value={tr.title} onChange={(e) => setTr({ ...tr, title: e.target.value })} data-testid="tr-title" />
              <input className={ip} placeholder="Presenter" value={tr.presenter} onChange={(e) => setTr({ ...tr, presenter: e.target.value })} data-testid="tr-presenter" />
              <select className={ip} value={tr.level} onChange={(e) => setTr({ ...tr, level: e.target.value })}>{["All", "Beginner", "Intermediate", "Advanced"].map((x) => <option key={x} className="bg-[#08090F]">{x}</option>)}</select>
              <input type="number" className={ip} placeholder="Duration (min)" value={tr.duration_min} onChange={(e) => setTr({ ...tr, duration_min: e.target.value })} data-testid="tr-duration" />
              {tr.kind === "seminar" && <label className="text-xs text-[#64748B] sm:col-span-1">Starts at<input type="datetime-local" className={`${ip} mt-1`} value={tr.starts_at ? tr.starts_at.slice(0, 16) : ""} onChange={(e) => setTr({ ...tr, starts_at: e.target.value ? new Date(e.target.value).toISOString() : "" })} data-testid="tr-starts" /></label>}
              {tr.kind === "recording" && <label className="text-xs text-[#64748B] sm:col-span-1">Recorded at<input type="datetime-local" className={`${ip} mt-1`} value={tr.recorded_at ? tr.recorded_at.slice(0, 16) : ""} onChange={(e) => setTr({ ...tr, recorded_at: e.target.value ? new Date(e.target.value).toISOString() : "" })} /></label>}
              <input className={`${ip} sm:col-span-2`} placeholder="URL (recording/link, optional)" value={tr.url} onChange={(e) => setTr({ ...tr, url: e.target.value })} data-testid="tr-url" />
              <textarea className={`${ip} sm:col-span-3`} rows={2} placeholder="Description" value={tr.desc} onChange={(e) => setTr({ ...tr, desc: e.target.value })} data-testid="tr-desc" />
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={saveTr} data-testid="tr-save" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#2DD4BF] text-[#030305] text-sm font-semibold"><FloppyDisk size={14} /> {trEdit ? "Update" : "Schedule"}</button>
              {trEdit && <button onClick={() => { setTr(TR_EMPTY); setTrEdit(null); }} className="px-4 py-2 rounded-full border border-white/15 text-[#94A3B8] text-sm">Cancel</button>}
            </div>
          </div>
          <div className="space-y-2">
            {trainings.length ? trainings.map((t) => (
              <div key={t.id} className="flex items-center justify-between rounded-lg border border-white/10 bg-[#0A0B10] px-4 py-3" data-testid={`tr-row-${t.id}`}>
                <div className="min-w-0">
                  <p className="text-sm text-white">{t.title} <span className="font-mono text-[10px] text-[#2DD4BF] uppercase">{t.kind}</span></p>
                  <p className="text-xs text-[#64748B] font-mono truncate max-w-lg">{t.presenter} · {t.level}{t.starts_at ? ` · ${new Date(t.starts_at).toLocaleString()}` : ""}{t.duration_min ? ` · ${t.duration_min}min` : ""}</p>
                </div>
                <div className="flex gap-3 shrink-0">
                  <button onClick={() => editTr(t)} data-testid={`tr-edit-${t.id}`} className="text-[#2DD4BF] text-xs hover:underline flex items-center gap-1"><PencilSimple size={13} /> Edit</button>
                  <button onClick={() => delTr(t.id)} data-testid={`tr-delete-${t.id}`} className="text-red-400 text-xs hover:underline">Delete</button>
                </div>
              </div>
            )) : <p className="text-sm text-[#64748B] font-mono">No trainings yet.</p>}
          </div>
        </div>
      )}
    </div>
  );
}


function PortfolioConfigs() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/portfolio-configs").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async (it) => {
    const { id, created_at, ...body } = it;
    try { await api.put(`/admin/portfolio-configs/${id}`, body); toast.success("Saved"); load(); }
    catch { toast.error("Save failed"); }
  };
  const add = async () => {
    try { await api.post("/admin/portfolio-configs", { name: "New Portfolio", description: "", min_cents: 10000, max_cents: 1000000, daily_rate_bps: 50, duration_months: 12, payout_options: ["daily", "staking", "hybrid"], status: "active", order: items.length + 1 }); load(); }
    catch { toast.error("Add failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/portfolio-configs/${id}`); load(); };
  const upd = (idx, k, v) => setItems(items.map((it, i) => i === idx ? { ...it, [k]: v } : it));
  return (
    <div data-testid="admin-portfolios">
      <div className="flex justify-between mb-4"><p className="text-sm text-[#94A3B8]">{items.length} configs</p><button onClick={add} data-testid="admin-add-portfolio" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-white text-sm hover:bg-white/5"><Plus size={15} /> Add Portfolio</button></div>
      <div className="space-y-4">
        {items.map((it, idx) => (
          <div key={it.id} className="rounded-xl border border-white/10 bg-[#0A0B10] p-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid={`admin-pcfg-${idx}`}>
            <label className="text-xs text-[#64748B]">Name<input className="w-full mt-1 rounded-lg border border-white/10 bg-[#08090F] px-3 py-2 text-sm text-white" value={it.name} onChange={(e) => upd(idx, "name", e.target.value)} /></label>
            <label className="text-xs text-[#64748B]">Min (cents)<input type="number" className="w-full mt-1 rounded-lg border border-white/10 bg-[#08090F] px-3 py-2 text-sm text-white" value={it.min_cents} onChange={(e) => upd(idx, "min_cents", Number(e.target.value))} /></label>
            <label className="text-xs text-[#64748B]">Max (cents)<input type="number" className="w-full mt-1 rounded-lg border border-white/10 bg-[#08090F] px-3 py-2 text-sm text-white" value={it.max_cents} onChange={(e) => upd(idx, "max_cents", Number(e.target.value))} /></label>
            <label className="text-xs text-[#64748B]">Daily rate (bps)<input type="number" className="w-full mt-1 rounded-lg border border-white/10 bg-[#08090F] px-3 py-2 text-sm text-white" value={it.daily_rate_bps} onChange={(e) => upd(idx, "daily_rate_bps", Number(e.target.value))} /></label>
            <label className="sm:col-span-2 lg:col-span-3 text-xs text-[#64748B]">Description<input className="w-full mt-1 rounded-lg border border-white/10 bg-[#08090F] px-3 py-2 text-sm text-white" value={it.description} onChange={(e) => upd(idx, "description", e.target.value)} /></label>
            <label className="text-xs text-[#64748B]">Status
              <select className="w-full mt-1 rounded-lg border border-white/10 bg-[#08090F] px-3 py-2 text-sm text-white" value={it.status} onChange={(e) => upd(idx, "status", e.target.value)}><option value="active" className="bg-[#08090F]">active</option><option value="inactive" className="bg-[#08090F]">inactive</option></select>
            </label>
            <div className="sm:col-span-2 lg:col-span-4 flex gap-2">
              <button onClick={() => save(it)} data-testid={`admin-pcfg-save-${idx}`} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#2DD4BF] text-[#030305] text-sm font-semibold"><FloppyDisk size={14} /> Save</button>
              <button onClick={() => del(it.id)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-white/15 text-red-400 text-sm"><Trash size={14} /> Delete</button>
            </div>
            <p className="text-[10px] text-[#64748B] font-mono lg:col-span-4">Rate {(it.daily_rate_bps / 100).toFixed(2)}%/day · ${(it.min_cents / 100).toFixed(0)}–${(it.max_cents / 100).toFixed(0)}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function WithdrawalsAdmin() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/withdrawals").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const move = async (w, to) => {
    let tx_hash = null;
    if (to === "COMPLETED") tx_hash = window.prompt("Transaction hash (optional):") || null;
    const reason = to === "REJECTED" || to === "FAILED" ? (window.prompt("Reason:") || "") : "";
    try { await api.post(`/admin/withdrawals/${w.withdrawal_id}/transition`, { to, reason, tx_hash }); toast.success(`→ ${to}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-3" data-testid="admin-withdrawals">
      {items.length ? items.map((w) => (
        <div key={w.id} className="rounded-xl border border-white/10 bg-[#0A0B10] p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-white font-mono text-sm">{w.withdrawal_id} · ${(w.amount_cents / 100).toFixed(2)} {w.asset}</p>
              <p className="text-xs text-[#64748B] font-mono">{w.wallet} wallet → {w.address?.slice(0, 14)}… · {w.network}</p>
            </div>
            <span className="font-mono text-xs text-[#2DD4BF]">{w.status}</span>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {(WD_NEXT[w.status] || []).map((to) => (
              <button key={to} onClick={() => move(w, to)} data-testid={`admin-wd-${to}`} className="px-3 py-1.5 rounded-full border border-white/15 text-white text-xs hover:bg-white/5">{to.replace("_", " ")}</button>
            ))}
            {!WD_NEXT[w.status]?.length && <span className="text-xs text-[#64748B] font-mono">Terminal state</span>}
          </div>
        </div>
      )) : <p className="text-sm text-[#64748B] font-mono">No withdrawals.</p>}
    </div>
  );
}

function KycAdmin() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/kyc").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const review = async (id, approve) => {
    const reason = approve ? "" : (window.prompt("Rejection reason:") || "");
    try { await api.post(`/admin/kyc/${id}/review`, { approve, reason }); toast.success(approve ? "Approved" : "Rejected"); load(); }
    catch { toast.error("Failed"); }
  };
  return (
    <div className="space-y-3" data-testid="admin-kyc">
      {items.length ? items.map((k) => (
        <div key={k.id} className="rounded-xl border border-white/10 bg-[#0A0B10] p-5 flex flex-wrap items-center justify-between gap-3">
          <div><p className="text-white text-sm">{k.email}</p><p className="text-xs text-[#64748B]">{k.doc_type} · {k.status}</p></div>
          {k.status === "pending" ? (
            <div className="flex gap-2">
              <button onClick={() => review(k.id, true)} data-testid={`admin-kyc-approve-${k.id}`} className="px-3 py-1.5 rounded-full bg-[#2DD4BF] text-[#030305] text-xs font-semibold">Approve</button>
              <button onClick={() => review(k.id, false)} className="px-3 py-1.5 rounded-full border border-white/15 text-red-400 text-xs">Reject</button>
            </div>
          ) : <span className={`font-mono text-xs ${k.status === "verified" ? "text-emerald-300" : "text-red-300"}`}>{k.status}</span>}
        </div>
      )) : <p className="text-sm text-[#64748B] font-mono">No KYC submissions.</p>}
    </div>
  );
}

const TK_STATES = ["OPEN", "PENDING", "RESOLVED", "CLOSED"];
function SupportAdmin() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const load = () => api.get(`/admin/support/tickets${filter ? `?status=${filter}` : ""}`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [filter]); // eslint-disable-line
  useEffect(() => { if (active) { const f = items.find((t) => t.ticket_id === active.ticket_id); if (f) setActive(f); } }, [items]); // eslint-disable-line
  const send = async (status) => {
    try {
      await api.post(`/admin/support/tickets/${active.ticket_id}/reply`, { message: reply, status });
      toast.success(`Ticket → ${status}`); setReply(""); load();
    } catch { toast.error("Failed"); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="admin-support">
      <div>
        <div className="flex gap-2 mb-3 flex-wrap">
          <button onClick={() => setFilter("")} className={`px-3 py-1.5 rounded-full text-xs ${filter === "" ? "bg-[#2DD4BF] text-[#030305]" : "border border-white/15 text-[#94A3B8]"}`}>All</button>
          {TK_STATES.map((s) => <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-full text-xs ${filter === s ? "bg-[#2DD4BF] text-[#030305]" : "border border-white/15 text-[#94A3B8]"}`}>{s}</button>)}
        </div>
        <div className="space-y-2">
          {items.length ? items.map((t) => (
            <button key={t.ticket_id} onClick={() => setActive(t)} data-testid={`admin-ticket-${t.ticket_id}`}
              className={`w-full flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left ${active?.ticket_id === t.ticket_id ? "border-[#2DD4BF]/40 bg-[#2DD4BF]/[0.05]" : "border-white/10 bg-[#0A0B10] hover:border-white/20"}`}>
              <div className="min-w-0"><p className="text-sm text-white truncate">{t.subject}</p><p className="text-[10px] font-mono text-[#64748B] mt-0.5">{t.ticket_id} · {t.email} · {t.category}</p></div>
              <span className="shrink-0 font-mono text-[10px] text-[#2DD4BF]">{t.status}</span>
            </button>
          )) : <p className="text-sm text-[#64748B] font-mono">No tickets.</p>}
        </div>
      </div>
      <div className="rounded-xl border border-white/10 bg-[#0A0B10] p-5">
        {active ? (
          <div data-testid="admin-ticket-thread">
            <p className="font-bold text-white">{active.subject}</p>
            <p className="text-[10px] font-mono text-[#64748B] mb-3">{active.ticket_id} · {active.email}</p>
            <div className="space-y-2 max-h-[300px] overflow-y-auto mb-3">
              {(active.messages || []).map((m, i) => (
                <div key={i} className={`rounded-lg border p-3 ${m.role === "admin" ? "border-[#2DD4BF]/20 bg-[#2DD4BF]/[0.05]" : "border-white/10 bg-black/20"}`}>
                  <p className="text-[10px] font-mono uppercase text-[#64748B] mb-1">{m.role === "admin" ? "Support" : m.by}</p>
                  <p className="text-sm text-[#cbd5e1] whitespace-pre-wrap">{m.body}</p>
                </div>
              ))}
            </div>
            <textarea data-testid="admin-ticket-reply" rows={3} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply to member…" className="w-full rounded-lg border border-white/10 bg-[#08090F] px-3.5 py-2.5 text-sm text-white focus:border-[#2DD4BF]/50 focus:outline-none mb-2" />
            <div className="flex flex-wrap gap-2">
              <button onClick={() => send("PENDING")} data-testid="admin-ticket-send" className="px-4 py-2 rounded-full bg-[#2DD4BF] text-[#030305] text-xs font-semibold">Reply (Pending)</button>
              <button onClick={() => send("RESOLVED")} className="px-4 py-2 rounded-full border border-emerald-400/30 text-emerald-300 text-xs">Resolve</button>
              <button onClick={() => send("CLOSED")} className="px-4 py-2 rounded-full border border-white/15 text-[#94A3B8] text-xs">Close</button>
            </div>
          </div>
        ) : <div className="grid place-items-center h-full py-16 text-sm text-[#64748B] font-mono">Select a ticket to respond.</div>}
      </div>
    </div>
  );
}


const CONTENT_TABS = [
  { key: "plans", label: "License Plans", icon: Tag },
  { key: "strategies", label: "Strategies", icon: Stack },
  { key: "deploy_modes", label: "Deploy Modes", icon: SlidersHorizontal },
  { key: "faqs", label: "FAQs", icon: Question },
  { key: "roadmap", label: "Roadmap", icon: MapTrifold },
  { key: "events", label: "Events", icon: CalendarBlank },
  { key: "team", label: "Team", icon: UsersThree },
  { key: "exchanges", label: "Exchanges", icon: Buildings },
  { key: "partner_tiers", label: "Partner Tiers", icon: Handshake },
  { key: "legal", label: "Legal Docs", icon: Scales },
  { key: "announcements", label: "Announcements", icon: Megaphone },
];

const inputCls =
  "w-full rounded-lg border border-white/10 bg-[#08090F] px-3 py-2 text-sm text-white placeholder:text-[#475569] focus:border-[#2DD4BF]/50 focus:outline-none transition-colors";

function isComplex(v) {
  return v !== null && typeof v === "object";
}

function FieldEditor({ fieldKey, value, onChange }) {
  if (typeof value === "boolean") {
    return (
      <label className="flex items-center gap-2 py-1">
        <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} className="accent-[#2DD4BF] h-4 w-4" />
        <span className="text-sm text-[#cbd5e1] capitalize">{fieldKey}</span>
      </label>
    );
  }
  if (isComplex(value)) {
    return (
      <div>
        <label className="block text-xs font-mono uppercase tracking-widest text-[#64748B] mb-1">{fieldKey} (JSON)</label>
        <textarea
          rows={5}
          defaultValue={JSON.stringify(value, null, 2)}
          onBlur={(e) => {
            try {
              onChange(JSON.parse(e.target.value));
            } catch {
              toast.error(`Invalid JSON in "${fieldKey}"`);
            }
          }}
          className={`${inputCls} font-mono text-xs`}
        />
      </div>
    );
  }
  const isLong = typeof value === "string" && value.length > 60;
  return (
    <div>
      <label className="block text-xs font-mono uppercase tracking-widest text-[#64748B] mb-1">{fieldKey}</label>
      {isLong ? (
        <textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} />
      ) : (
        <input
          value={value ?? ""}
          onChange={(e) => {
            const raw = e.target.value;
            onChange(typeof value === "number" && raw !== "" && !isNaN(Number(raw)) ? Number(raw) : raw);
          }}
          className={inputCls}
        />
      )}
    </div>
  );
}

function ItemCard({ ctype, item, onSaved, onDeleted }) {
  const [draft, setDraft] = useState(item);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const keys = Object.keys(draft).filter((k) => k !== "id" && k !== "created_at");

  const save = async () => {
    setSaving(true);
    try {
      const { id, created_at, ...body } = draft;
      const { data } = await api.put(`/content/${ctype}/${item.id}`, body);
      onSaved(data);
      setEditing(false);
      toast.success("Saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/content/${ctype}/${item.id}`);
      onDeleted(item.id);
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const title = draft.title || draft.name || draft.q || draft.phase || draft.label || `Item ${item.id.slice(-4)}`;

  return (
    <div className="rounded-xl border border-white/10 bg-[#0A0B10] p-5" data-testid={`admin-item-${item.id}`}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-semibold text-white truncate">{title}</p>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={() => setEditing(!editing)} className="grid place-items-center h-8 w-8 rounded-md border border-white/10 text-[#94A3B8] hover:text-[#2DD4BF] transition-colors" title="Edit">
            <PencilSimple size={15} />
          </button>
          <button onClick={del} data-testid={`admin-delete-${item.id}`} className="grid place-items-center h-8 w-8 rounded-md border border-white/10 text-[#94A3B8] hover:text-red-400 transition-colors" title="Delete">
            <Trash size={15} />
          </button>
        </div>
      </div>
      {editing && (
        <div className="mt-4 space-y-3">
          {keys.map((k) => (
            <FieldEditor key={k} fieldKey={k} value={draft[k]} onChange={(v) => setDraft({ ...draft, [k]: v })} />
          ))}
          <button onClick={save} disabled={saving} data-testid={`admin-save-${item.id}`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2DD4BF] text-[#030305] text-sm font-semibold hover:bg-white transition-colors disabled:opacity-60">
            <FloppyDisk size={15} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      )}
    </div>
  );
}

function ContentManager({ ctype }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    getContent(ctype).then((d) => { setItems(d); setLoading(false); }).catch(() => setLoading(false));
  }, [ctype]);

  useEffect(() => { load(); }, [load]);

  const addNew = async () => {
    const template = items[0]
      ? Object.fromEntries(Object.keys(items[0]).filter((k) => k !== "id" && k !== "created_at").map((k) => {
          const v = items[0][k];
          return [k, typeof v === "number" ? 0 : typeof v === "boolean" ? false : Array.isArray(v) ? [] : isComplex(v) ? {} : ""];
        }))
      : { title: "New item", order: items.length + 1 };
    try {
      const { data } = await api.post(`/content/${ctype}`, template);
      setItems([...items, data]);
      toast.success("Item added — expand to edit");
    } catch {
      toast.error("Add failed");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-[#94A3B8]">{items.length} item(s)</p>
        <button onClick={addNew} data-testid="admin-add-item" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-white text-sm hover:bg-white/5 transition-colors">
          <Plus size={15} /> Add Item
        </button>
      </div>
      {loading ? (
        <p className="text-[#64748B] font-mono text-sm">Loading…</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((it) => (
            <ItemCard
              key={it.id}
              ctype={ctype}
              item={it}
              onSaved={(u) => setItems((prev) => prev.map((p) => (p.id === u.id ? u : p)))}
              onDeleted={(id) => setItems((prev) => prev.filter((p) => p.id !== id))}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SettingsManager() {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    getSettings().then((s) => {
      const { key, ...rest } = s;
      setText(JSON.stringify(rest, null, 2));
    }).catch(() => {});
  }, []);
  const save = async () => {
    let body;
    try { body = JSON.parse(text); } catch { toast.error("Invalid JSON"); return; }
    setSaving(true);
    try {
      await api.put("/settings", body);
      toast.success("Settings saved");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  return (
    <div data-testid="admin-settings">
      <p className="text-sm text-[#94A3B8] mb-4">Edit site settings (contact info, wallet addresses, social links, disclaimer, market coverage).</p>
      <textarea rows={22} value={text} onChange={(e) => setText(e.target.value)} className={`${inputCls} font-mono text-xs`} />
      <button onClick={save} disabled={saving} data-testid="admin-settings-save" className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#2DD4BF] text-[#030305] text-sm font-semibold hover:bg-white transition-colors disabled:opacity-60">
        <FloppyDisk size={15} /> {saving ? "Saving…" : "Save Settings"}
      </button>
    </div>
  );
}

function Submissions() {
  const [contacts, setContacts] = useState([]);
  const [partners, setPartners] = useState([]);
  useEffect(() => {
    api.get("/contact/submissions").then((r) => setContacts(r.data)).catch(() => {});
    api.get("/partners/applications").then((r) => setPartners(r.data)).catch(() => {});
  }, []);
  return (
    <div className="space-y-8" data-testid="admin-submissions">
      <div>
        <h3 className="font-bold text-white mb-3">Contact Submissions ({contacts.length})</h3>
        <div className="space-y-2">
          {contacts.map((c) => (
            <div key={c.id} className="rounded-lg border border-white/10 bg-[#0A0B10] p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-white font-semibold">{c.name} · <span className="text-[#2DD4BF]">{c.inquiry_type}</span></span>
                <span className="font-mono text-xs text-[#64748B]">{c.email}</span>
              </div>
              <p className="text-sm text-[#94A3B8] mt-2">{c.message}</p>
            </div>
          ))}
          {!contacts.length && <p className="text-[#64748B] text-sm font-mono">No submissions yet.</p>}
        </div>
      </div>
      <div>
        <h3 className="font-bold text-white mb-3">Partner Applications ({partners.length})</h3>
        <div className="space-y-2">
          {partners.map((p) => (
            <div key={p.id} className="rounded-lg border border-white/10 bg-[#0A0B10] p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-white font-semibold">{p.name} · <span className="text-[#0E9E90]">{p.program}</span></span>
                <span className="font-mono text-xs text-[#64748B]">{p.email} · {p.country}</span>
              </div>
              {p.message && <p className="text-sm text-[#94A3B8] mt-2">{p.message}</p>}
            </div>
          ))}
          {!partners.length && <p className="text-[#64748B] text-sm font-mono">No applications yet.</p>}
        </div>
      </div>
    </div>
  );
}

function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/analytics/summary").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <p className="text-[#64748B] font-mono text-sm">Loading…</p>;
  return (
    <div data-testid="admin-analytics">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[["Total Events", data.total_events], ["Members", data.members], ["Contacts", data.contacts], ["Partner Apps", data.partner_applications]].map(([l, v]) => (
          <div key={l} className="rounded-xl border border-white/10 bg-[#0A0B10] p-5">
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#64748B]">{l}</p>
            <p className="font-mono text-2xl text-[#2DD4BF] mt-1.5">{v}</p>
          </div>
        ))}
      </div>
      <h3 className="font-bold text-white mb-3">Events by name</h3>
      <div className="rounded-xl border border-white/10 bg-[#0A0B10] divide-y divide-white/5">
        {data.by_name.map((e) => (
          <div key={e._id} className="flex items-center justify-between px-5 py-3">
            <span className="text-sm text-[#cbd5e1] font-mono">{e._id || "unknown"}</span>
            <span className="font-mono text-sm text-white">{e.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Admin() {
  const [tab, setTab] = useState("analytics");
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => { await logout(); navigate("/"); };

  const TABS = [
    { key: "analytics", label: "Analytics", icon: Gauge },
    { key: "portfolios", label: "Portfolios", icon: Cpu },
    { key: "withdrawals", label: "Withdrawals", icon: ArrowLineDown },
    { key: "kyc", label: "KYC Review", icon: Fingerprint },
    { key: "support", label: "Support Tickets", icon: Question },
    { key: "signals", label: "Signals & VIP", icon: Broadcast },
    { key: "resources", label: "Resources", icon: BookOpen },
    ...CONTENT_TABS,
    { key: "settings", label: "Site Settings", icon: GearSix },
    { key: "submissions", label: "Submissions", icon: Tray },
  ];

  const active = TABS.find((t) => t.key === tab);

  return (
    <div className="min-h-screen bg-[#030305] text-white flex">
      <aside className="hidden md:flex w-60 shrink-0 flex-col border-r border-white/10 bg-[#08090F] fixed inset-y-0" data-testid="admin-sidebar">
        <Link to="/" className="flex items-center gap-2.5 h-16 px-6 border-b border-white/10">
          <span className="grid place-items-center h-8 w-8 rounded-md border border-[#2DD4BF]/40 bg-[#2DD4BF]/10">
            <span className="h-3 w-3 rounded-sm bg-[#2DD4BF] rotate-45" />
          </span>
          <span className="font-black text-sm">Admin<span className="text-[#2DD4BF]">CMS</span></span>
        </Link>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-nav-${t.key}`}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                tab === t.key ? "bg-[#2DD4BF]/10 text-[#2DD4BF] border border-[#2DD4BF]/20" : "text-[#94A3B8] hover:text-white hover:bg-white/5"
              }`}
            >
              <t.icon size={17} weight={tab === t.key ? "fill" : "regular"} /> {t.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10 space-y-1">
          <Link to="/" className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-[#94A3B8] hover:text-white hover:bg-white/5 transition-colors"><House size={17} /> View site</Link>
          <button onClick={doLogout} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-[#94A3B8] hover:text-white hover:bg-white/5 transition-colors"><SignOut size={17} /> Sign out</button>
        </div>
      </aside>

      <div className="flex-1 md:ml-60">
        <header className="h-16 border-b border-white/10 flex items-center justify-between px-5 md:px-8 sticky top-0 bg-[#030305]/80 backdrop-blur-xl z-10">
          <h1 className="text-lg font-bold">{active?.label}</h1>
          <span className="text-sm text-[#94A3B8]">{user?.email}</span>
        </header>

        <div className="md:hidden flex gap-2 overflow-x-auto p-3 border-b border-white/10">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} className={`shrink-0 px-3 py-1.5 rounded-full text-xs ${tab === t.key ? "bg-[#2DD4BF] text-[#030305]" : "border border-white/15 text-[#94A3B8]"}`}>{t.label}</button>
          ))}
        </div>

        <main className="p-5 md:p-8">
          {tab === "analytics" && <Analytics />}
          {tab === "portfolios" && <PortfolioConfigs />}
          {tab === "withdrawals" && <WithdrawalsAdmin />}
          {tab === "kyc" && <KycAdmin />}
          {tab === "support" && <SupportAdmin />}
          {tab === "signals" && <SignalsAdmin />}
          {tab === "resources" && <ResourcesAdmin />}
          {tab === "settings" && <SettingsManager />}
          {tab === "submissions" && <Submissions />}
          {CONTENT_TABS.some((t) => t.key === tab) && <ContentManager ctype={tab} key={tab} />}
        </main>
      </div>
    </div>
  );
}
